import JSONModel from "sap/ui/model/json/JSONModel";
import BaseController from "../controller/BaseController";
import { EnvironmentService } from "../service/Environment.service";
import { Uploader$UploadCompletedEvent } from "sap/m/upload/Uploader";
import UploadSet, { UploadSet$BeforeUploadStartsEvent, UploadSet$FileSizeExceededEvent, UploadSet$FileTypeMismatchEvent } from "sap/m/upload/UploadSet";
import { Button$PressEvent } from "sap/m/Button";
import Control from "sap/ui/core/Control";
import ResponsivePopover from "sap/m/ResponsivePopover";
import Camera from "../camera/Camera";
import { UploadSetItem$OpenPressedEvent, UploadSetItem$RemovePressedEvent } from "sap/m/upload/UploadSetItem";
import MessageBox from "sap/m/MessageBox";
import { ErrorHandler } from "../service/ErrorHandler.service";
import Event from "sap/ui/base/Event";
import { FileSettings } from "../model/models";

/**
 * @namespace com.unitrade.crm.blocks
 */
export default class FileAttachmentsBlock extends BaseController {
  private _pPopover: Control = null;
  private _metaModel: JSONModel;
  private _attachments: JSONModel;

  onInit(): void {
    this._metaModel = new JSONModel({
      uploadUrl: "",
      authToken: "",
      busy: false,
      uploadBusy: false,
      baseUrl: EnvironmentService.getBaseUrl(),
      enabled: false,
    });

    const uploadSet = this.byId("file-attachments-block-upload-set") as UploadSet;
    uploadSet.getDefaultFileUploader().setIconOnly(true);
    uploadSet.getDefaultFileUploader().setIcon("sap-icon://attachment");

    this._attachments = new JSONModel([]);
    this.setModel(this._metaModel, "meta");
    this.setModel(this._attachments, "attachments");
    this.onModelContextChange = this.onModelContextChange.bind(this);
    this.getView().attachModelContextChange(null, this.onModelContextChange);
  }

  async onModelContextChange(oEvent: Event) {
    await this._loadAttachments();
  }

  private _loadFileSettings = async (): Promise<FileSettings> => {
    return $.getJSON(`${EnvironmentService.getBaseUrl()}/document/settings/file_settings`);
  };

  private _loadAttachments = async () => {
    // TODO: das ist schlecht. besser url zum speichern übergeben und
    // attachments unabhängig vom aufrufer machen
    const oCustomerModel = this.getModel("Customer");
    const oBesuchsberichtModel = this.getModel("besuchsbericht");
    const oBelegModel = this.getModel("belegkopf");
    let url: string = "";
    if (oCustomerModel) {
      const kundId = oCustomerModel.getProperty("/id");
      const kundLfd = oCustomerModel.getProperty("/lfd");
      if (!kundId || !kundLfd) return;
      url = this._getUrlForKunde(kundId, kundLfd);
    } else if (oBesuchsberichtModel) {
      const besuchsberichtId = oBesuchsberichtModel.getProperty("/id");
      if (!besuchsberichtId) return;
      url = this._getUrlForBesuchsbericht(besuchsberichtId);
    } else if (oBelegModel) {
      const belegart = oBelegModel.getProperty("/art");
      const belegnr = oBelegModel.getProperty("/id");
      if (!belegart || !belegnr) return;
      url = this._getUrlForBeleg(belegart, belegnr);
    } else {
      MessageBox.warning("Unbekannter Datentyp: Dateianhänge nicht unterstützt.");
      return;
    }

    if (url) {
      this._metaModel.setProperty("/uploadUrl", url);
      try {
        this._metaModel.setProperty("/busy", true);
        const filesettings = await this._loadFileSettings();

        if (filesettings && filesettings.enabled) {
          this._metaModel.setProperty("/enabled", filesettings.enabled);
          this._metaModel.setProperty("/maxFileSize", filesettings.MAX_DOC_SIZE / 1000);
          this._metaModel.setProperty("/fileTypes", filesettings.ALLOWED_DOCUMENT_EXTENSIONS);
          await this._attachments.loadData(url);
        } else {
          this._metaModel.setProperty("/enabled", false);
        }
      } catch (ex: any) {
        MessageBox.show("Fehler beim Laden von Anhängen: " + ex.statusText);
      } finally {
        this._metaModel.setProperty("/busy", false);
      }
    }
  };

  protected fileSizeExceeded = (oEvent: UploadSet$FileSizeExceededEvent) => {
    const item = oEvent.getParameter("item");
    const limitInMb: number = this._metaModel.getProperty("/maxFileSize");
    const limitInBytes = limitInMb * 1000000;
    MessageBox.alert(`${item.getFileName()} ist zu groß. Das Limit liegt bei ${this.humanFileSize(limitInBytes, true)}.`);
  };

  protected fileTypeMismatch = (oEvent: UploadSet$FileTypeMismatchEvent) => {
    const item = oEvent.getParameter("item");
    const fileTypes: string[] = this._metaModel.getProperty("/fileTypes");
    MessageBox.alert(`${item.getFileName()} ist nicht erlaubt. Folgende Dateitypen sind zulässig: ${fileTypes.join(", ")}.`);
  };

  private _getUrlForBesuchsbericht = (id: number) => {
    return `${EnvironmentService.getBaseUrl()}/document/besuchsbericht/${id}`;
  };

  private _getUrlForKunde = (kundId: number, kundLfd: number) => {
    return `${EnvironmentService.getBaseUrl()}/document/kunde/${kundId}/${kundLfd}`;
  };

  private _getUrlForBeleg = (belegart: string, belegnr: number) => {
    return `${EnvironmentService.getBaseUrl()}/document/beleg/${belegart}/${belegnr}`;
  };

  beforeUploadStarts = (oEvent: UploadSet$BeforeUploadStartsEvent) => {
    const token = localStorage.getItem("auth_token");
    this._metaModel.setProperty("/token", token);
  };

  private _getHeaders(): Headers {
    const headers = new Headers();
    headers.append("Authorization", "Bearer " + localStorage.getItem("auth_token"));

    return headers;
  }

  openFilePressed = (oEvent: UploadSetItem$OpenPressedEvent) => {
    oEvent.preventDefault(); // do not open link in new tab
    oEvent.cancelBubble();
    
    const item = oEvent.getParameter("item");
    const oBindingCOntext = item.getBindingContext("attachments");
    const attachment = this._attachments.getProperty(oBindingCOntext.getPath());
    const url = `${this._metaModel.getProperty("/baseUrl")}/document/${attachment.id}`;
    this.downloadFile(url)
      .then((result) => {
        const blob = result as Blob;
        const url = window.URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', attachment.dateiName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((ex) => {
        ErrorHandler.handleError(ex, { title: ex.message });
      })

      return false;
  }

  downloadFile = (url: string) => {
    let settings: JQueryAjaxSettings = {
      url: url,
      method: "GET",
      xhrFields: {
        responseType: "blob"
      },
      beforeSend: function(req: any) {
        req.setRequestHeader("Accept", "application/octet-stream");
      },
    };

    return new Promise ((resolve, reject) => {
      $.ajax(settings)
        .done((result, textStatus, request) => {
          resolve(result);
        })
        .fail((err) => {
          alert(JSON.stringify(err));
          reject(err);
        })
    });
  }

  removeFilePressed = async (oEvent: UploadSetItem$RemovePressedEvent) => {
    oEvent.preventDefault(); // do not show dialog...
    const item = oEvent.getParameter("item");
    MessageBox.show(`${item.getFileName()} löschen?`, {
      icon: MessageBox.Icon.QUESTION,
      actions: [MessageBox.Action.DELETE, MessageBox.Action.NO],
      emphasizedAction: MessageBox.Action.NO,
      onClose: (async (answer: any) => {
        if (answer == MessageBox.Action.DELETE) {
          try {
            this._metaModel.setProperty("/busy", true);
            const item = oEvent.getParameter("item");
            const oBindingCOntext = item.getBindingContext("attachments");
            const attachment = this._attachments.getProperty(oBindingCOntext.getPath());
            const url = `${this._metaModel.getProperty("/baseUrl")}/document/${attachment.id}`;

            const response = await fetch(url, {
              method: "DELETE",
              headers: this._getHeaders(),
            });

            if (response.status > 299) {
              ErrorHandler.handleError(response);
            } else {
              MessageBox.success(`Datei ${item.getFileName()} erfolgreich gelöscht.`);
            }
          } catch (ex: any) {
            ErrorHandler.handleError(ex);
          } finally {
            this._metaModel.setProperty("/busy", false);
            this._loadAttachments();
          }
        }
      }).bind(this),
    });
  };

  filesizeFormatter = (size: number) => {
    return this.humanFileSize(size);
  };

  uploadCompleted = async (oEvent: Uploader$UploadCompletedEvent) => {
    const response: any = oEvent.getParameter("responseXHR");

    if (response && response.status > 299) {
      ErrorHandler.handleError(response, { message: "Fehler beim Upload" });
    }
    const uploadSet = oEvent.getSource().getParent() as UploadSet;
    uploadSet.removeAllIncompleteItems();

    console.log("upload-Completed", response);

    await this._loadAttachments();
  };

  /**
   * Format bytes as human-readable text.
   *
   * @param bytes Number of bytes.
   * @param si True to use metric (SI) units, aka powers of 1000. False to use
   *           binary (IEC), aka powers of 1024.
   * @param dp Number of decimal places to display.
   *
   * @return Formatted string.
   */
  humanFileSize(bytes: number, si = false, dp = 1) {
    // see https://stackoverflow.com/a/14919494
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
      return bytes + " B";
    }

    const units = si ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"] : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
    let u = -1;
    const r = 10 ** dp;

    do {
      bytes /= thresh;
      ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

    return bytes.toFixed(dp) + " " + units[u];
  }
}
